import { useFragmentsViewCapture } from '@app/modules/fragments/hooks/useFragmentsViewCapture';
import { DateTimeStr } from '@mi-front/core';
import { Box } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { useFragment } from '../../../hooks/useFragment';
import { TranscriptionBlocksList } from '../blocks/TranscriptionBlocksList';
import { useFragmentTranscription } from '../hooks/useFragmentTranscription';
import { useTranscriptionInteractionsStore } from '../hooks/useTranscriptionInteractionsStore';
import { useTranscriptionPlaybackSync } from '../hooks/useTranscriptionPlaybackSync';
import { useTranscriptionWordEditing } from '../hooks/useTranscriptionWordEditing';
import { TranscriptionSearchNoMatches } from '../search/TranscriptionSearchNoMatches';
import { useTranscriptionSearch } from '../search/useTranscriptionSearch';
import { TranscriptionEmpty } from './state/TranscriptionEmpty';
import { TranscriptionToolbar } from './TranscriptionToolbar';

/**
 * Renders loaded transcription content with search and follow-playback behavior.
 */
export function TranscriptionContent() {
  const { fragment } = useFragment();
  const { blocks } = useFragmentTranscription(fragment);
  const fragmentsCapture = useFragmentsViewCapture();
  const { seekToTimeSeconds, isBlockActive, isWordHighlighted } =
    useTranscriptionPlaybackSync(fragment);
  const wordEditing = useTranscriptionWordEditing({
    fragmentId: fragment.id,
    blocks,
    capture: {
      onEditStart: fragmentsCapture.transcriptionWordEditStart,
      onEditConfirm: fragmentsCapture.transcriptionWordEditConfirm,
      onEditCancel: fragmentsCapture.transcriptionWordEditCancel,
    },
  });

  const {
    query: searchQuery,
    setQuery: setSearchQuery,
    normalizedQuery: searchNormalizedQuery,
    matchWordIds,
    displayBlocks,
    noMatches,
  } = useTranscriptionSearch(blocks);

  const hasBlocks = blocks.length > 0;
  const activeBlockId = blocks.find((b) => isBlockActive(b.segment))?.id ?? null;
  const setInteractionState = useTranscriptionInteractionsStore(
    (state) => state.setInteractionState,
  );
  const setInteractionHandlers = useTranscriptionInteractionsStore(
    (state) => state.setInteractionHandlers,
  );
  const isEditMode = useTranscriptionInteractionsStore((state) => state.isEditMode);
  const showWordEfficiency = useTranscriptionInteractionsStore((state) => state.showWordEfficiency);
  const enableFollowPlayback = useTranscriptionInteractionsStore(
    (state) => state.enableFollowPlayback,
  );
  const disableFollowPlayback = useTranscriptionInteractionsStore(
    (state) => state.disableFollowPlayback,
  );
  const setIsEditMode = useTranscriptionInteractionsStore((state) => state.setIsEditMode);
  const setShowWordEfficiency = useTranscriptionInteractionsStore(
    (state) => state.setShowWordEfficiency,
  );
  const resetInteractionsStore = useTranscriptionInteractionsStore((state) => state.reset);

  const handleSearchQueryChange = (value: string) => {
    fragmentsCapture.transcriptionSearch({ query: value });
    setSearchQuery(value);
  };

  const handleSyncIconClick = useCallback(() => {
    fragmentsCapture.transcriptionSyncToggle({ enabled: true });
    enableFollowPlayback();
  }, [enableFollowPlayback, fragmentsCapture]);

  const handleWordEfficiencyClick = useCallback(() => {
    const nextEnabled = !showWordEfficiency;
    fragmentsCapture.transcriptionWordEfficiencyToggle({ enabled: nextEnabled });
    setShowWordEfficiency(nextEnabled);
  }, [fragmentsCapture, setShowWordEfficiency, showWordEfficiency]);

  const seekToTimeSecondsAndFollow = useCallback(
    (timeSeconds: number, chunkTime: DateTimeStr) => {
      enableFollowPlayback();
      seekToTimeSeconds(timeSeconds, chunkTime);
    },
    [enableFollowPlayback, seekToTimeSeconds],
  );

  const handleWordClick = useCallback(
    (wordStartSeconds: number, chunkTime: DateTimeStr) => {
      fragmentsCapture.transcriptionWordClick({
        wordStartSeconds,
        chunkTime,
      });
      seekToTimeSecondsAndFollow(wordStartSeconds, chunkTime);
    },
    [fragmentsCapture, seekToTimeSecondsAndFollow],
  );

  /** Keeps Zustand in sync immediately so the controlled inline input does not flash a stale value. */
  const handleEditingDraftChange = useCallback(
    (value: string) => {
      wordEditing.setDraftText(value);
      setInteractionState({ editingDraftText: value });
    },
    [setInteractionState, wordEditing.setDraftText],
  );

  const handleWordEditStart = useCallback(
    (wordId: string, text: string) => {
      wordEditing.startEditing(wordId, text);
      setInteractionState({
        editingWordId: wordId,
        editingDraftText: text,
        wordSaveError: null,
      });
    },
    [setInteractionState, wordEditing.startEditing],
  );

  const handleWordEditCancel = useCallback(() => {
    wordEditing.cancelEditing();
    setInteractionState({
      editingWordId: null,
      editingDraftText: '',
      wordSaveError: null,
    });
  }, [setInteractionState, wordEditing.cancelEditing]);

  const handleEditModeClick = useCallback(() => {
    const nextEnabled = !isEditMode;
    if (!nextEnabled) {
      handleWordEditCancel();
    }
    fragmentsCapture.transcriptionEditModeToggle({ enabled: nextEnabled });
    setIsEditMode(nextEnabled);
  }, [fragmentsCapture, handleWordEditCancel, isEditMode, setIsEditMode]);

  useEffect(() => {
    if (searchNormalizedQuery.length > 0) {
      disableFollowPlayback();
    }
  }, [disableFollowPlayback, searchNormalizedQuery]);

  useEffect(() => {
    setInteractionState({
      matchWordIds,
      editingWordId: wordEditing.editingWordId,
      editingDraftText: wordEditing.draftText,
      isWordSaving: wordEditing.isSaving,
      wordSaveError: wordEditing.saveError,
    });
  }, [
    matchWordIds,
    setInteractionState,
    wordEditing.draftText,
    wordEditing.editingWordId,
    wordEditing.isSaving,
    wordEditing.saveError,
  ]);

  useEffect(() => {
    setInteractionHandlers({
      onEditingDraftChange: handleEditingDraftChange,
      onWordEditStart: handleWordEditStart,
      onWordEditSave: wordEditing.saveEditing,
      onWordEditCancel: handleWordEditCancel,
      onWordClick: handleWordClick,
      onSeekSegment: (block) => seekToTimeSecondsAndFollow(block.segment.start, block.chunkTime),
      isBlockActive,
      isWordHighlighted,
      onEditModeClick: handleEditModeClick,
      onSyncIconClick: handleSyncIconClick,
      onWordEfficiencyClick: handleWordEfficiencyClick,
    });
  }, [
    handleEditModeClick,
    handleSyncIconClick,
    handleWordEfficiencyClick,
    handleWordClick,
    isBlockActive,
    isWordHighlighted,
    seekToTimeSecondsAndFollow,
    handleEditingDraftChange,
    handleWordEditCancel,
    handleWordEditStart,
    setInteractionHandlers,
    wordEditing.saveEditing,
  ]);

  useEffect(() => resetInteractionsStore, [resetInteractionsStore]);

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {hasBlocks ? (
        <>
          <TranscriptionToolbar
            searchQuery={searchQuery}
            onSearchQueryChange={handleSearchQueryChange}
          />
          {noMatches ? (
            <TranscriptionSearchNoMatches />
          ) : (
            <TranscriptionBlocksList blocks={displayBlocks} activeBlockId={activeBlockId} />
          )}
        </>
      ) : (
        <TranscriptionEmpty />
      )}
    </Box>
  );
}
